import VueRouter, { RouteConfig } from 'vue-router';
import accountRoutes from './account/account';
import accreditationRoutes from './accreditation';
import { app } from '@store/modules/app';
import checkoutRoutes from './checkout/checkout';
import educationRoutes from './education';
import investmentGoalRoutes from './investment-goals';
import iraAddFundsRoutes from './ira/ira-add-funds';
import iraCompleteOrderRoutes from './ira/ira-complete-order';
import iraCompleteRolloverRoutes from './ira/ira-complete-rollover';
import iraGeneralRoutes from './ira/ira-general';
import iraReacknowledgeRolloverRoutes from './ira/ira-reacknowledge-rollover';
import iraSignupRoutes from './ira/ira-signup';
import iraVerifyRoutes from './ira/ira-verify';
import legalRoutes from './legal';
import loginRoutes from './login/login';
import microFeedbackRoutes from './micro-feedback';
import modularLandingPageRoutes from './modular-landing-pages';
import noteInvestmentRoutes from './note-investment';
import { pageViewed } from '@utils/analytics';
import questionnaireRoutes from './questionnaire';
import redemptionRequestRoutes from './redemption-request';
import settingsRoutes from './account/settings';
import startInvestingRoutes from './start-investing';
import { user } from '@store/modules/user';
import Vue from 'vue';
import VueMeta from 'vue-meta';

Vue.use(VueRouter);
Vue.use(VueMeta);

/* eslint-disable @typescript-eslint/explicit-function-return-type */
const routes: RouteConfig[] = [
	{
		path: `/about`,
		name: 'about',
		component: () => import(/* webpackChunkName: "marketing-site-about" */ '@views/marketing-site/about.vue')
	},
	{
		path: `/press`,
		name: 'press',
		component: () => import(/* webpackChunkName: "marketing-site-press" */ '@views/marketing-site/press.vue')
	},
	{
		path: `/`,
		name: 'home',
		component: () => import(/* webpackChunkName: "marketing-site-home" */ '@views/marketing-site/home.vue')
	},
	{
		path: `/how-it-works`,
		name: 'how-it-works',
		component: () =>
			import(/* webpackChunkName: "marketing-site-how-it-works" */ '@views/marketing-site/how-it-works.vue')
	},
	{
		path: `/investments`,
		name: 'investments',
		component: () =>
			import(/* webpackChunkName: "marketing-site-investments" */ '@views/marketing-site/investments.vue')
	},
	{
		path: `/investments/core-plans/compare`,
		name: 'compare-core-plans',
		component: () =>
			import(
				/* webpackChunkName: "marketing-site-compare-core-plans" */ '@views/marketing-site/compare-core-plans.vue'
			)
	},
	{
		path: `/investments/advanced-plans/:planSlug`,
		props: true,
		name: 'plan-detail-pages',
		component: () =>
			import(/* webpackChunkName: "marketing-site-plan-detail-pages" */ '@views/marketing-site/advanced-plan.vue')
	},
	{
		path: `/jobs`,
		name: 'careers',
		component: () => import(/* webpackChunkName: "marketing-site-careers" */ '@views/marketing-site/careers.vue')
	},
	{
		path: `/assets`,
		alias: '/assets/:location',
		name: 'assets',
		component: () => import(/* webpackChunkName: "marketing-site-assets" */ '@views/marketing-site/assets.vue')
	},
	{
		path: `/invitation-program/landing`,
		name: 'referral-landing',
		component: () => import(/* webpackChunkName: "referral-landing" */ '@views/referral/referral-landing.vue')
	},
	{
		path: `/nps`,
		name: 'thank-you-nps',
		component: () => import(/* webpackChunkName: "thank-you-nps" */ '@views/nps/nps-landing.vue')
	},
	{
		path: `/404`,
		name: '404',
		component: () => import(/* webpackChunkName: "404" */ '@views/error/not-found-404.vue')
	},
	{
		path: `/forbidden`,
		name: 'forbidden',
		component: () => import(/* webpackChunkName: "forbidden" */ '@views/error/forbidden.vue')
	},
	{
		path: `/guides/real-estate-investing-guide`,
		name: 'rei-guide',
		component: () =>
			import(/* webpackChunkName: "marketing-site-rei-guide" */ '@views/marketing-site/rei-guide.vue')
	},
	{
		path: `/why-private-real-estate`,
		name: 'why-private-real-estate',
		component: () =>
			import(
				/* webpackChunkName: "marketing-site-why-private-real-estate" */ '@views/marketing-site/why-private-real-estate.vue'
			)
	},
	{
		path: `/real-estate-strategies`,
		name: 'real-estate-strategies',
		component: () =>
			import(
				/* webpackChunkName: "marketing-site-real-estate-strategies" */ '@views/marketing-site/real-estate-strategies.vue'
			)
	},
	{
		path: `/strategy`,
		name: 'strategy',
		component: () =>
			import(
				/* webpackChunkName: "landing-page-strategy-overview" */ '@views/marketing-site/strategy-overview.vue'
			)
	},
	{
		path: `/ereits`,
		name: 'ereits',
		component: () => import(/* webpackChunkName: "marketing-site-ereits" */ '@views/marketing-site/ereits.vue')
	},
	{
		path: `/track-record`,
		name: 'track-record',
		component: () =>
			import(/* webpackChunkName: "marketing-site-track-record" */ '@views/marketing-site/track-record.vue')
	},
	{
		path: `/the-distance`,
		name: 'the-distance',
		component: () =>
			import(/* webpackChunkName: "newsletter-landing-page" */ '@views/newsletter/newsletter-landing-page.vue')
	},
	{
		path: `/reits/checkout/success`,
		name: 'reits-checkout-success',
		component: () => import(/* webpackChunkName: "reits-checkout-success" */ '@views/checkout/checkout-success.vue')
	},
	{
		path: `/signup/verify-signup-checkout`,
		name: 'verify-signup-checkout',
		component: () => import(/* webpackChunkName: "verify-signup-checkout" */ '@views/checkout/checkout-success.vue')
	},
	{
		path: `/welcome-video`,
		name: 'welcome-video',
		component: () => import(/* webpackChunkName: "welcome-video" */ '@views/account/welcome-video.vue')
	},
	{
		path: `/webview/home`,
		name: 'webview-home',
		component: () => import(/* webpackChunkName: "marketing-site-home-webview" */ '@views/webview/home.vue')
	},
	{
		path: `/webview/plan-details/:slug`,
		name: 'webview-plan-details',
		component: () => import(/* webpackChunkName: "plan-details-webview" */ '@views/webview/plan-details.vue')
	},
	{
		path: `/webview/service-level-details/:slug`,
		name: 'webview-service-level-details',
		component: () =>
			import(/* webpackChunkName: "service-level-details-webview" */ '@views/webview/service-level-details.vue')
	},
	{
		path: `/webview/track-record-annual-returns`,
		name: 'webview-track-record-annual-returns',
		component: () =>
			import(
				/* webpackChunkName: "webview-track-record-annual-returns" */ '@views/webview/track-record-annual-returns.vue'
			)
	},
	{
		path: `/webview/legal-disclosure`,
		name: 'webview-legal-disclosure',
		component: () =>
			import(/* webpackChunkName: "webview-legal-disclosure" */ '@views/webview/legal-disclosure.vue')
	},
	{
		path: `/webview/track-record-assumptions`,
		name: 'webview-track-record-assumptions',
		component: () =>
			import(
				/* webpackChunkName: "webview-track-record-assumptions" */ '@views/webview/track-record-assumptions.vue'
			)
	},
	{
		path: `/story/:id`,
		name: 'story-player',
		component: () => import(/* webpackChunkName: "story-player" */ '@views/stories/story-player.vue')
	},
	{
		path: `/user-research/confirm`,
		name: 'user-research-confirm',
		component: () =>
			import(/* webpackChunkName: "user-research-confirm" */ '@views/user-research/user-research-confirm.vue')
	},
	{
		path: '/investor-update/:investorUpdateId/view',
		name: 'investor-update',
		beforeEnter: async (to, from, next) => {
			const isId = /^\d+$/.test(to.params.investorUpdateId);
			if (isId) {
				next();
			} else {
				next({ name: '404' });
			}
		},
		component: () =>
			import(/* webpackChunkName: "investor-update-detail" */ '@views/investor-update/investor-update-detail.vue')
	},
	{
		path: `/get-iphone-app`,
		name: 'get-iphone-app',
		component: () => import(/* webpackChunkName: "get-iphone-app" */ '@views/mobile/get-iphone-app.vue')
	},
	{
		path: `/get-android-app`,
		name: 'get-android-app',
		component: () => import(/* webpackChunkName: "get-android-app" */ '@views/mobile/get-android-app.vue')
	},
	{
		path: `/logout-success`,
		name: 'logout-success',
		component: () => import(/* webpackChunkName: "logout-logout-success" */ '@views/logout/logout-success.vue')
	},
	{
		path: `/offerings`,
		name: 'offerings',
		component: () => import(/* webpackChunkName: "offerings" */ '@views/offerings/offerings.vue')
	},
	{
		path: `/contact`,
		name: 'contact',
		component: () => import(/* webpackChunkName: "contact" */ '@views/contact/contact.vue')
	},
	...accountRoutes,
	...settingsRoutes,
	accreditationRoutes,
	...questionnaireRoutes,
	startInvestingRoutes,
	...investmentGoalRoutes,
	iraSignupRoutes,
	iraAddFundsRoutes,
	iraCompleteOrderRoutes,
	iraCompleteRolloverRoutes,
	...iraGeneralRoutes,
	iraReacknowledgeRolloverRoutes,
	iraVerifyRoutes,
	...legalRoutes,
	...educationRoutes,
	...modularLandingPageRoutes,
	noteInvestmentRoutes,
	...checkoutRoutes,
	...loginRoutes,
	...redemptionRequestRoutes,
	...microFeedbackRoutes
];

const router = new VueRouter({
	mode: 'history',
	base: `${process.env.VUE_APP_BASE_PATH}`,
	scrollBehavior(to, from, savedPosition) {
		if (savedPosition) {
			return savedPosition;
		} else {
			if (to.hash) {
				return {
					selector: to.hash
				};
			}
			return { x: 0, y: 0 };
		}
	},
	routes
});
/* eslint-enable @typescript-eslint/explicit-function-return-type */

router.beforeEach(async (to, from, next): Promise<void> => {
	if (!to.matched.length) {
		next('/404');
	} else {
		const isAuthenticated =
			window.fundriseApp.oAuthToken !== '' && window.fundriseApp.requiresAdditionalAuthentication === false;

		app.SET_IS_AUTHENTICATED(isAuthenticated);
		app.UPDATE_ROUTE_TRANSITION({ to, from });

		if (isAuthenticated && !user.user) {
			await user.getUser();
		}
		next();
	}
});

router.beforeResolve((to, from, next) => {
	pageViewed(to, from);
	next();
});

export default router;
